import {NormalizedEnvironment} from '../../_web/utils/environment'
import {
  COGNITO_LOGIN_CALLBACK_ROUTE,
  COGNITO_LOGOUT_CALLBACK_ROUTE
} from '../constants'
import {amplifyElalProduction} from './configurations/elal-production'
import {amplifyElalStaging} from './configurations/elal-staging'
import {amplifyEtripProduction} from './configurations/etrip-production'
import {amplifyEtripStaging} from './configurations/etrip-staging'
import {amplifyHotelixProduction} from './configurations/hotelix-production'
import {amplifyHotelixStaging} from './configurations/hotelix-staging'
import {amplifyKiwiProduction} from './configurations/kiwi-production'
import {amplifyKiwiStaging} from './configurations/kiwi-staging'
import {amplifyNovaairProduction} from './configurations/novaair-production'
import {amplifyNovaairStaging} from './configurations/novaair-staging'
import {amplifyVioProduction} from './configurations/vio-production'
import {amplifyVioStaging} from './configurations/vio-staging'

export interface AmplifyConfig {
  aws_project_region: string
  aws_cognito_identity_pool_id: string
  aws_cognito_region: string
  aws_user_pools_id: string
  aws_user_pools_web_client_id: string
  oauth: Oauth
  federationTarget: string
  aws_cognito_username_attributes: string[]
  aws_cognito_social_providers: string[]
  aws_cognito_signup_attributes?: string[]
  aws_cognito_mfa_configuration: string
  aws_cognito_mfa_types?: string[]
  aws_cognito_password_protection_settings: AwsCognitoPasswordProtectionSettings
  aws_cognito_verification_mechanisms?: string[]
  aws_user_files_s3_bucket: string
  aws_user_files_s3_bucket_region: string
  cookieStorage?: CookieStorage
}

interface AwsCognitoPasswordProtectionSettings {
  passwordPolicyMinLength: number
  passwordPolicyCharacters: string[]
}

interface Oauth {
  domain: string
  scope: string[]
  redirectSignIn: string
  redirectSignOut: string
  responseType: string
}

export interface CookieStorage {
  domain: string
  path: string
  expires: number
  sameSite: string
  secure: boolean
}

export interface Params {
  aws_user_pools_id?: string
  aws_cognito_identity_pool_id?: string
  aws_user_pools_web_client_id?: string
  oauth_domain?: string
  redirectSignIn?: string
  redirectSignOut?: string
  aws_user_files_s3_bucket?: string
}

export const getCognitoCookieDomain = (hostName: string) => {
  const parts = hostName.split('.')

  // https://kube-review.internal.vio.com returned as kube-review.internal.vio.com
  if (hostName.includes('kube-review.internal.vio.com')) {
    return 'kube-review.internal.vio.com'
  }

  // Vercel preview deployments; scoped to the preview suffix so staging-pool
  // cookies never land on .vio.com next to production sessions.
  if (hostName.includes('vercel.internal.vio.com')) {
    return 'vercel.internal.vio.com'
  }

  // https://re-search-sc-47-c77d35ff.shared-prod.fih.io returned as shared-prod.fih.io
  if (hostName.includes('.fih.io')) {
    return parts.slice(-3).join('.')
  }

  // Two-part TLDs (e.g. hotelix.co.il): the registrable domain is the last
  // three labels. `slice(-2)` would return the public suffix `co.il`, which
  // browsers reject as a cookie domain, so the site and `secure.` checkout
  // subdomain must both resolve to `hotelix.co.il`.
  if (hostName.endsWith('.co.il')) {
    return parts.slice(-3).join('.')
  }

  // El Al and NovaAir live on vio.com subdomains with their own user pools.
  // Scoping the cookie to the brand host keeps their sessions off www.vio.com
  // while still covering the secure.<brand>.vio.com checkout.
  if (hostName.endsWith('elal.vio.com')) {
    return 'elal.vio.com'
  }

  if (hostName.endsWith('novaair.vio.com')) {
    return 'novaair.vio.com'
  }

  // https://www.vio.com returned as vio.com
  return parts.slice(-2).join('.')
}

const generateConfig = ({
  aws_user_pools_id = '',
  aws_cognito_identity_pool_id = '',
  aws_user_pools_web_client_id = '',
  oauth_domain = '',
  redirectSignIn = '',
  redirectSignOut = '',
  aws_user_files_s3_bucket = '',
  hostname
}: Params & {hostname: string}): AmplifyConfig => {
  const region = 'eu-west-1'

  return {
    aws_user_pools_id,
    aws_user_pools_web_client_id,
    aws_cognito_identity_pool_id,
    aws_user_files_s3_bucket,
    aws_project_region: region,
    aws_cognito_region: region,
    aws_user_files_s3_bucket_region: region,
    oauth: {
      domain: oauth_domain,
      redirectSignIn,
      redirectSignOut,
      scope: [
        'aws.cognito.signin.user.admin',
        'email',
        'openid',
        'phone',
        'profile'
      ],
      responseType: 'code'
    },
    federationTarget: 'COGNITO_USER_POOLS',
    aws_cognito_username_attributes: ['PHONE_NUMBER'],
    aws_cognito_social_providers: ['FACEBOOK', 'GOOGLE', 'APPLE'],
    aws_cognito_mfa_configuration: 'OFF',
    aws_cognito_password_protection_settings: {
      passwordPolicyMinLength: 8,
      passwordPolicyCharacters: [
        'REQUIRES_LOWERCASE',
        'REQUIRES_UPPERCASE',
        'REQUIRES_NUMBERS',
        'REQUIRES_SYMBOLS'
      ]
    },
    cookieStorage: {
      domain: getCognitoCookieDomain(hostname),
      path: '/',
      expires: 365,
      sameSite: 'strict',
      secure: isSecure(hostname)
    },
    aws_cognito_signup_attributes: [],
    aws_cognito_mfa_types: []
  }
}

const isSecure = (hostname: string) => !hostname.includes('localhost')

/**
 * Returns the Amplify configuration preset that matches the provided origin/hostname.
 * @param origin - The origin URL or hostname
 * @returns Amplify configuration object
 */
export const getConfigByHostName = (origin: string) => {
  if (origin.includes('hotelix.co.il')) return amplifyHotelixProduction

  if (origin.includes('elal.vio.com'))
    return origin.includes('tst.') ? amplifyElalStaging : amplifyElalProduction

  if (origin.includes('novaair.vio.com'))
    return origin.includes('tst.')
      ? amplifyNovaairStaging
      : amplifyNovaairProduction

  if (
    origin.includes('tst.') ||
    origin.includes('kube-review.internal.vio.com') ||
    origin.includes('vercel.internal.vio.com') ||
    origin.includes('.e2e.sez.cp.internal.vio.com')
  )
    return amplifyVioStaging

  if (origin.includes('findhotel.net') || origin.includes('vio.com'))
    return amplifyVioProduction

  if (
    origin.includes('etrip.net') ||
    origin.includes('dealbase.com') ||
    origin.includes('goseek.com')
  )
    return amplifyEtripProduction

  if (origin.includes('fih.io') && origin.includes('etrip'))
    return amplifyEtripStaging

  if (origin.includes('fih.io') && origin.includes('hotelix'))
    return amplifyHotelixStaging

  if (origin.includes('fih.io') && origin.includes('elal'))
    return amplifyElalStaging

  if (origin.includes('fih.io') && origin.includes('novaair'))
    return amplifyNovaairStaging

  if (origin.includes('kiwi.com')) return amplifyKiwiProduction

  return amplifyVioStaging
}

export const getConfigByBrandAndEnvironment = (
  brand: string,
  environment: NormalizedEnvironment
): Partial<AmplifyConfig> | undefined => {
  switch (brand) {
    case 'kiwi':
      return environment === 'production'
        ? amplifyKiwiProduction
        : amplifyKiwiStaging
    case 'hotelix':
      return environment === 'production'
        ? amplifyHotelixProduction
        : amplifyHotelixStaging
    case 'elal':
      return environment === 'production'
        ? amplifyElalProduction
        : amplifyElalStaging
    case 'novaair':
      return environment === 'production'
        ? amplifyNovaairProduction
        : amplifyNovaairStaging
    default:
      return undefined
  }
}

export const getAmplifyConfig = (
  origin: string,
  brand: string,
  environment: NormalizedEnvironment
): AmplifyConfig => {
  const configByBrand = getConfigByBrandAndEnvironment(brand, environment)
  const configByHostName = getConfigByHostName(origin)
  const config = configByBrand || configByHostName
  const {hostname} = new URL(origin)
  return generateConfig({
    ...config,
    redirectSignIn: `${origin}${COGNITO_LOGIN_CALLBACK_ROUTE}`,
    redirectSignOut: `${origin}${COGNITO_LOGOUT_CALLBACK_ROUTE}`,
    hostname
  })
}
